
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ProductData } from '@/hooks/useProductDetail';
import { formatDate, formatPrice } from '@/hooks/useProductDetail';
import { Heart, MessageCircle, Package, Calendar, Tag, MapPin, Truck, CreditCard, ShoppingCart, User, Phone } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useMessageSeller } from '@/hooks/useMessageSeller';
import { useAuth } from '@/context/AuthContext';
import { ProductStats } from './ProductStats';
import { useToast } from '@/hooks/use-toast';
import { ShareMenu } from './ShareMenu';
import ContactOptionsDialog from './ContactOptionsDialog';
import { createProductUrl } from '@/utils/slugUtils';

interface ProductInfoSidebarProps {
  product: ProductData;
  isFavorited: boolean | undefined;
  onFavoriteToggle: () => void;
  onBuyNow?: () => void;
}

export function ProductInfoSidebar({ 
  product, 
  isFavorited,
  onFavoriteToggle,
  onBuyNow
}: ProductInfoSidebarProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  // Pass both product ID and seller ID to useMessageSeller
  const handleMessageSeller = useMessageSeller(
    product.id, 
    product.user_id || product.seller?.id
  );
  
  // Create a wrapper function that doesn't require parameters
  const handleInternalChat = () => {
    // Create a synthetic event for the handler
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
    } as React.MouseEvent;
    
    handleMessageSeller(syntheticEvent);
  };

  // Helper function to format full phone number
  const formatFullPhoneNumber = (phone: string | null, countryCode: string | null): string => {
    if (!phone) return '';
    
    const code = countryCode || '+355'; // Default to Albania country code
    return `${code} ${phone}`;
  };
  
  const showCondition = !!product.condition;
  const showStock = product.stock !== undefined && product.stock > 0;
  
  // Get delivery information
  const hasShipping = product.delivery_option !== 'pickup_only';
  const hasPickup = product.delivery_option !== 'shipping_only';
  const showShippingCost = hasShipping && product.shipping_cost !== undefined && product.shipping_cost > 0;
  
  // Check if the current user is the seller
  const isOwnProduct = user && product.user_id === user.id;
  
  // Check if this is an unauthenticated listing (has contact_email but not by current user)
  const isUnauthenticatedListing = !!product.contact_email && product.is_authenticated_listing === false;
  
  // Handle Buy Now button click
  const handleBuyNow = () => {
    if (onBuyNow) {
      onBuyNow();
    }
  };

  // Handle view seller profile - only available for authenticated listings
  const handleViewProfile = () => {
    if (product.user_id && !isUnauthenticatedListing) {
      navigate(`/user-profile?user=${product.user_id}`);
    } else {
      toast({
        description: "Ky shitës nuk ka një profil të regjistruar.",
      });
    }
  };
  
  // Create share text for the product with SEO-friendly URL
  const getShareText = () => {
    return `Shiko ${product.title} në Tregu.al - ${formatPrice(product.price, product.currency)}`;
  };
  
  // Get the first product image for sharing
  const getShareImage = () => {
    return product.images && product.images.length > 0 ? product.images[0] : '';
  };

  // Create SEO-friendly share URL
  const getShareUrl = () => {
    const productUrl = createProductUrl(product.id, product.title);
    return `${window.location.origin}${productUrl}`;
  };
  
  return (
    <Card>
      <CardContent className="p-6">
        {/* Price Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-brand-500">
              {formatPrice(product.price, product.currency)}
            </span>
            <div className="flex space-x-2 items-center">
              {product.is_premium && (
                <Badge className="bg-yellow-500 text-xs">Premium</Badge>
              )}
              {product.is_price_negotiable && (
                <Badge className="bg-blue-500 text-xs">Çmim i negociueshëm</Badge>
              )}
            </div>
          </div>
          {showShippingCost && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center">
              <CreditCard className="h-4 w-4 mr-1 text-gray-500" />
              Kostoja e Transportit: {formatPrice(product.shipping_cost || 0, product.currency)}
            </p>
          )}
          
          {/* Add Product Stats */}
          <ProductStats productId={String(product.id)} className="mt-2" />
        </div>
        
        <Separator className="my-4" />
        
        {/* Product Details */}
        <div className="space-y-3 mb-6">
          {showStock && (
            <div className="flex items-center text-sm">
              <Package className="h-4 w-4 mr-2 text-gray-500" />
              <span>
                {product.stock === 1 ? 
                  '1 artikull në stok' : 
                  `${product.stock} artikuj në stok`}
              </span>
            </div>
          )}
          
          <div className="flex items-center text-sm">
            <Calendar className="h-4 w-4 mr-2 text-gray-500" />
            <span>Publikuar: {formatDate(product.created_at)}</span>
          </div>
          
          <div className="flex items-center text-sm">
            <MapPin className="h-4 w-4 mr-2 text-gray-500" />
            <span>{product.location}</span>
          </div>
          
          {product.category && (
            <div className="flex items-center text-sm">
              <Tag className="h-4 w-4 mr-2 text-gray-500" />
              <Link 
                to={`/search?category=${product.category.id}`}
                className="text-blue-600 hover:underline"
              >
                {product.category.name}
              </Link>
            </div>
          )}
          
          {showCondition && (
            <div className="flex items-center text-sm">
              <Badge variant="outline" className="font-normal">
                {product.condition === 'new' && 'E re'}
                {product.condition === 'used' && 'E përdorur'}
                {product.condition === 'like_new' && 'Si e re'}
                {product.condition === 'fair' && 'E pranueshme'}
              </Badge>
            </div>
          )}
          
          {/* Delivery options */}
          <div className="flex items-center text-sm">
            <Truck className="h-4 w-4 mr-2 text-gray-500" />
            <span>
              {product.delivery_option === 'shipping_and_pickup' && 'Dërgimi dhe Tërheqja'}
              {product.delivery_option === 'pickup_only' && 'Vetëm Tërheqje'}
              {product.delivery_option === 'shipping_only' && 'Vetëm Dërgimi'}
            </span>
          </div>
          
          {/* Negotiable price */}
          {product.is_price_negotiable && (
            <div className="flex items-center text-sm">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                Çmimi është i negociueshëm
              </Badge>
            </div>
          )}
          
          {/* Phone number section - Show full phone number with country code */}
          {product.phone && (
            <div className="flex items-center text-sm">
              <Phone className="h-4 w-4 mr-2 text-green-500" />
              <a 
                href={`tel:${formatFullPhoneNumber(product.phone, product.phone_country_code)}`}
                className="font-medium text-green-600 hover:text-green-700"
              >
                {formatFullPhoneNumber(product.phone, product.phone_country_code)}
              </a>
            </div>
          )}
        </div>
        
        <Separator className="my-4" />
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          {/* Display info message if it's the user's own product */}
          {isOwnProduct && (
            <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
              Ky është produkti juaj, nuk mund të blini produktin tuaj.
            </div>
          )}
          
          {/* Add Buy Now button when shipping is available and it's not user's product */}
          {hasShipping && showStock && !isOwnProduct && !isUnauthenticatedListing && (
            <Button 
              onClick={handleBuyNow}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Bli Tani
            </Button>
          )}
          
          {/* Unified Contact Button - Don't show for own products */}
          {!isOwnProduct && (
            <ContactOptionsDialog
              phone={product.phone}
              phoneCountryCode={product.phone_country_code}
              whatsappEnabled={product.whatsapp_enabled}
              viberEnabled={product.viber_enabled}
              showInternalChat={!isUnauthenticatedListing}
              onInternalChat={handleInternalChat}
            >
              <Button className="w-full bg-brand-500 hover:bg-brand-600 text-white">
                <MessageCircle className="mr-2 h-4 w-4" />
                Kontakto Shitësin
              </Button>
            </ContactOptionsDialog>
          )}
          
          {/* Share menu with SEO-friendly URL */}
          <ShareMenu 
            title={product.title}
            url={getShareUrl()}
            text={getShareText()}
            image={getShareImage()}
          />
          
          {/* Add View Profile button - not for unauthenticated listings */}
          {!isUnauthenticatedListing && (
            <Button 
              onClick={handleViewProfile}
              variant="outline" 
              className="w-full border-gray-300 text-gray-700 dark:text-gray-300"
            >
              <User className="mr-2 h-4 w-4" />
              Shiko Profilin
            </Button>
          )}
          
          {/* Only allow favorite toggle for others' products */}
          {!isOwnProduct && (
            <Button 
              onClick={onFavoriteToggle}
              variant="outline" 
              className={`w-full border-gray-300 ${isFavorited ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}
            >
              <Heart className={`mr-2 h-4 w-4 ${isFavorited ? 'fill-red-500' : ''}`} />
              {isFavorited ? 'Hiq nga Të Preferuarat' : 'Shto tek Të Preferuarat'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
